import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft, MessageSquare } from 'lucide-react'
import { CreatorProfile } from '@adthlete/common'
import {
  MarketplaceProfile,
  PickedPosts,
  TalentSheetLoading,
  type BookPick,
} from '@adthlete/shared-frontend'
import { useAuth } from '@/hooks/useAuth'
import { bookingPath } from './PublicShop'
import { Button } from '@/components/ui/button'
import { SheetMessageDialog } from '@/components/talentSheets/SheetMessageDialog'
import { takeDraft } from '@/components/talentSheets/sheetMessageDraft'
import { useGetPublicSheetAthleteQuery } from '@/redux/talentSheetsApi'

// One athlete as shown on a shared talent sheet: their Content Shop (what the brand can book),
// with the posts the manager picked for the brand on top, and a way to message them.
const TalentSheetAthlete = () => {
  const { hashedId, athleteId } = useParams<{ hashedId: string; athleteId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [messageOpen, setMessageOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const { marketer } = useAuth()

  const { data, isLoading } = useGetPublicSheetAthleteQuery(
    { sheetId: hashedId!, athleteId: athleteId! },
    { skip: !hashedId || !athleteId },
  )

  // Back from signing in to send a message: reopen it with what they wrote
  useEffect(() => {
    if (searchParams.get('compose') !== '1' || !hashedId || !athleteId) return
    setDraft(takeDraft(hashedId, athleteId) ?? '')
    setMessageOpen(true)
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams, hashedId, athleteId])

  if (isLoading) return <TalentSheetLoading variant="brand" />

  const backToSheet = () => navigate(`/sheets/${hashedId}`)

  if (!data) {
    return (
      <div className="min-h-screen bg-linear-to-br from-[#F7F9F3] via-[#F4F7F0] to-[#EDF3EA] flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <p className="text-[#111827] text-xl font-semibold">Shop not found</p>
          <button onClick={backToSheet} className="text-sm text-[#0E7490] hover:underline">
            ← Back to talent sheet
          </button>
        </div>
      </div>
    )
  }

  const { athlete, sheet, shop } = data
  const firstName = athlete.name.split(' ')[0]
  // Book this: straight to the booking when signed in, through sign-in (or sign-up) otherwise
  const book = (pick: BookPick) => {
    if (!shop) return
    const to = bookingPath(shop.athlete.id, pick)
    navigate(marketer?.id ? to : `/signin?returnTo=${encodeURIComponent(to)}`)
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-[#F7F9F3] via-[#F4F7F0] to-[#EDF3EA]">
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur border-b border-foreground/10">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <button
            onClick={backToSheet}
            className="inline-flex min-w-0 items-center gap-1 text-sm text-gray-700 hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4 shrink-0" />
            <span className="truncate">Back to {sheet.title}</span>
          </button>
          <Button variant="lime" size="sm" onClick={() => setMessageOpen(true)}>
            <MessageSquare className="h-4 w-4 mr-2" />
            Message {firstName}
          </Button>
        </div>
      </div>

      {shop ? (
        <div className="mx-auto max-w-[1280px] p-4 pb-12 sm:p-6">
          <MarketplaceProfile
            data={shop}
            onBook={book}
            notes={{
              featured: athlete.highlights ? (
                <PickedPosts
                  brandName={athlete.highlights.brandName}
                  posts={athlete.highlights.posts}
                />
              ) : null,
            }}
          />
        </div>
      ) : (
        <CreatorProfile data={athlete} variant="brand" wrapperClassName="pb-12" />
      )}

      <SheetMessageDialog
        open={messageOpen}
        onOpenChange={setMessageOpen}
        sheetId={hashedId!}
        brandName={sheet.brandName}
        athlete={athlete}
        initialMessage={draft}
      />
    </div>
  )
}

export default TalentSheetAthlete
